// ── SmartTraQ Utilities ──
// Pure functions — no React, no side effects, no mutable state
import { ACCENTS, CURRENCIES, CAR, DEFAULT_ROLE_PERMS, LS_KEY } from './constants';

// ── Theme ──
export const applyAccent = (theme, accentKey) => {
  if (!accentKey || !ACCENTS[accentKey]) return theme;
  const a = ACCENTS[accentKey];
  return { ...theme, ac: a.ac, al: a.al, gr: a.gr };
};

// ── Currency ──
export const getRate = (cur, sett) => {
  if (!cur) return 1;
  const orgRates = sett?.currencyRates || {};
  if (orgRates[cur] !== undefined) return orgRates[cur];
  return CURRENCIES[cur]?.r || 1;
};

export const cvtAmt = (amt, from, to, sett) => {
  if (!amt) return amt;
  const src = from || "USD";
  if (src === to) return amt;
  const n = parseFloat(String(amt).replace(/[^0-9.]/g, ""));
  if (isNaN(n)) return amt;
  const fromRate = getRate(src, sett);
  const toRate = getRate(to, sett);
  const base = n / fromRate;
  const conv = base * toRate;
  return (CURRENCIES[to]?.s || "") + conv.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const toBase = (amt, fromCur, sett) => {
  if (!amt) return 0;
  const n = parseFloat(String(amt).replace(/[^0-9.]/g, ""));
  if (isNaN(n) || n === 0) return 0;
  const baseCur = sett?.baseCurrency || sett?.currency || "USD";
  const src = fromCur || "USD";
  if (src === baseCur) return n;
  const fromRate = getRate(src, sett) || 1;
  const baseRate = getRate(baseCur, sett) || 1;
  const result = n / fromRate * baseRate;
  return isNaN(result) ? 0 : result;
};

export const fmtAmt = (amt, cur, sett) => {
  if (!amt) return "—";
  const n = parseFloat(String(amt).replace(/[^0-9.]/g, ""));
  if (isNaN(n)) return "—";
  const sym = CURRENCIES[cur]?.s || "$";
  return sym + n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

export const fmtBase = (amt, sett) => {
  const baseCur = sett?.baseCurrency || sett?.currency || "USD";
  const sym = CURRENCIES[baseCur]?.s || "$";
  const n = parseFloat(amt || 0);
  return sym + (isNaN(n) ? 0 : n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

// ── Date & Time ──
export const prg = (s, e) => {
  if (!s || !e) return 0;
  const a = new Date(s), b = new Date(e), n = Date.now();
  return b <= a ? 100 : Math.min(100, Math.max(0, Math.round(((n - a) / (b - a)) * 100)));
};

export const fd = d => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : " - ";

// ── Settings-aware date formatter ──
export const fdSett = (d, sett) => {
  if (!d) return " - ";
  const dt = new Date(d + "T00:00:00");
  if (isNaN(dt)) return " - ";
  const fmt = sett?.dateFormat || "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  if (fmt === "DD/MM/YYYY") return dd + "/" + mm + "/" + yyyy;
  if (fmt === "YYYY-MM-DD") return yyyy + "-" + mm + "-" + dd;
  if (fmt === "MM/DD/YYYY") return mm + "/" + dd + "/" + yyyy;
  if (fmt === "DD.MM.YYYY") return dd + "." + mm + "." + yyyy;
  // Default: keep original short format
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const dte = d => d ? Math.ceil((new Date(d + "T00:00:00") - new Date()) / 864e5) : null;

export const relTime = d => {
  if (!d) return "";
  const ms = Date.now() - new Date(d).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return "Just now";
  const m = Math.floor(s / 60);
  if (m < 60) return m + "m ago";
  const h = Math.floor(m / 60);
  if (h < 24) return h + "h ago";
  const dy = Math.floor(h / 24);
  if (dy === 1) return "Yesterday";
  if (dy < 7) return dy + "d ago";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// ── IDs ──
export const nid = (p, arr) => {
  const pf = p + "-";
  // Global counter: max number across ALL items in the array regardless of prefix
  const nums = arr.filter(i => i.id).map(i => { const m = i.id.match(/(\d+)$/); return m ? parseInt(m[1]) || 0 : 0; });
  return pf + (Math.max(0, ...nums) + 1).toString().padStart(4, "0");
};

// ── Carriers ──
export const turl = (c, n) => {
  const x = CAR.find(k => k.n === c);
  if (!x || !x.u) return null;
  if (!n) return x.u;
  return x.d ? x.u + encodeURIComponent(n) : x.u;
};

export const isDeepLink = (c) => {
  const x = CAR.find(k => k.n === c);
  return x ? !!x.d : false;
};

// ── Permissions ──
export const hasp = (u, p, perms) => {
  if (!u) return false;
  const rp = (perms || DEFAULT_ROLE_PERMS)[u.role];
  if (!rp) return false;
  return !!rp[p];
};

// ── Local Storage ──
export const loadLS = () => {
  try { const d = localStorage.getItem(LS_KEY); return d ? JSON.parse(d) : null; } catch { return null; }
};

export const saveLS = (data) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify({ sett: data?.sett || {} })); } catch {}
};

// ── Amount in Words ──
export function amtWords(n, currency) {
  if (!n || isNaN(n)) return "";
  const ones=["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
  const tens=["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  const scales=["","Thousand","Million","Billion"];
  const num=Math.floor(Math.abs(n));
  if(num===0) return "Zero "+(currency||"Dollars")+" Only";
  const chunks=[];
  let rem=num;
  while(rem>0){chunks.push(rem%1000);rem=Math.floor(rem/1000)}
  const w3=(n3)=>{if(n3===0)return"";const h=Math.floor(n3/100),t=n3%100;let s="";if(h)s+=ones[h]+" Hundred";if(t<20)s+=(s?" ":"")+ones[t];else s+=(s?" ":"")+tens[Math.floor(t/10)]+(t%10?" "+ones[t%10]:"");return s};
  const parts=chunks.map((c,i)=>c?w3(c)+(scales[i]?" "+scales[i]:""):"").filter(Boolean).reverse().join(" ");
  const cents=Math.round((Math.abs(n)-num)*100);
  const curNames={USD:"US Dollars",AED:"UAE Dirhams",EUR:"Euros",GBP:"British Pounds",SAR:"Saudi Riyals",QAR:"Qatari Riyals",KWD:"Kuwaiti Dinars",BHD:"Bahraini Dinars",OMR:"Omani Rials",EGP:"Egyptian Pounds",CNY:"Chinese Yuan",INR:"Indian Rupees",JPY:"Japanese Yen"};
  const cn=curNames[currency]||currency||"Dollars";
  return parts+" "+cn+(cents>0?" and "+cents+"/100":"")+" Only";
}


// ── Delete vs Archive Decision Engine ──
// Returns { ok: boolean, reasons: string[] }
// ok=true → safe to hard-delete (entity has no downstream dependencies)
// ok=false → archive only (reasons explain why)
export function canDelete(entity, type, ctx) {
  const { orders=[], invoices=[], rfqs=[], ships=[], movements=[], tradeDocs=[], tasks=[], tokens=[], vendorTokens={}, prods=[] } = ctx || {};
  const reasons = [];

  if (type === "order") {
    const id = entity.id;
    if (entity.apr !== "draft") reasons.push("Not in draft state");
    if (entity.stg !== "new_inquiry") reasons.push("Moved past inquiry stage");
    if ((entity.payments || []).length > 0) reasons.push("Has payments");
    if ((entity.items || []).filter(it => it.desc?.trim()).length > 0 && entity.stg !== "new_inquiry") reasons.push("Has line items in active stage");
    if (invoices.some(i => i.oid === id)) reasons.push("Has linked invoices");
    if (rfqs.some(r => r.oid === id)) reasons.push("Has linked RFQs");
    if (ships.some(s => s.oid === id)) reasons.push("Has linked shipments");
    if (movements.some(m => m.orderId === id)) reasons.push("Has stock movements");
    if (tradeDocs.some(d => d.orderId === id)) reasons.push("Has trade documents");
    if (tokens.some(t => t.order_display_id === id && !t.revoked)) reasons.push("Customer portal shared");
    if (tasks.some(t => t.refId === id && t.type === "order")) reasons.push("Has linked tasks");
    if ((entity.comments || []).length > 0) reasons.push("Has comments");
    if ((entity.attachments || []).length > 0) reasons.push("Has attachments");
  }

  else if (type === "customer") {
    const name = entity.co;
    if (orders.some(o => o.cust === name && !o.archived)) reasons.push("Has linked orders");
    if (invoices.some(i => i.cust === name)) reasons.push("Has linked invoices");
    if (tasks.some(t => t.refId === entity.id && t.type === "customer")) reasons.push("Has linked tasks");
  }

  else if (type === "vendor") {
    const name = entity.co;
    if (orders.some(o => o.vend === name && !o.archived)) reasons.push("Has linked orders");
    if (rfqs.some(r => (r.vends || []).includes(name))) reasons.push("Has linked RFQs");
    if (prods.some(p => p.vend === name)) reasons.push("Has linked products");
    if (tasks.some(t => t.refId === entity.id && t.type === "vendor")) reasons.push("Has linked tasks");
    // Check vendor portal tokens with messages or submitted responses
    const vTks = Object.values(vendorTokens).flat().filter(t => t.vendor_name === name);
    if (vTks.some(t => (t.messages || []).length > 0 || t.response_status === "submitted")) reasons.push("Has portal messages or quotes");
  }

  else if (type === "product") {
    const qty = parseFloat(entity.stock_qty) || 0;
    if (qty !== 0) reasons.push("Has stock on hand (" + qty + ")");
    if (movements.some(m => m.prodId === entity.id || m.prodUid === entity._uid)) reasons.push("Has stock movements");
    if (orders.some(o => (o.items || []).some(it => it.desc?.toLowerCase() === entity.nm?.toLowerCase()))) reasons.push("Referenced in order line items");
    if (tasks.some(t => t.refId === entity.id && t.type === "product")) reasons.push("Has linked tasks");
  }

  else if (type === "shipment") {
    if (entity.st !== "processing") reasons.push("Shipment is " + entity.st);
    const linkedOrd = entity.oid ? orders.find(o => o.id === entity.oid) : null;
    if (linkedOrd && linkedOrd.apr !== "draft") reasons.push("Linked order is not draft");
    if (tasks.some(t => t.refId === entity.id && t.type === "shipment")) reasons.push("Has linked tasks");
  }

  else if (type === "rfq") {
    if (entity.st === "confirmed" || entity.st === "billed" || entity.st === "received") reasons.push("Confirmed as PO");
    if (rfqs.some(r => r._prevId === entity.id)) reasons.push("Has downstream PO");
    if (invoices.some(i => i.linkedInvoice === entity.id)) reasons.push("Has linked bills");
    // Check vendor portal with messages or submitted quotes
    const rfqTks = vendorTokens[entity.id] || [];
    if (rfqTks.some(t => (t.messages || []).length > 0)) reasons.push("Has portal messages");
    if (rfqTks.some(t => t.response_status === "submitted")) reasons.push("Has submitted vendor quotes");
  }

  else if (type === "invoice") {
    if (entity.status !== "draft") reasons.push("Invoice is not draft (status: " + entity.status + ")");
    if ((entity.payments || []).length > 0) reasons.push("Has recorded payments");
    if (invoices.some(i => i._prevId === entity.id)) reasons.push("Has downstream invoice");
  }

  return { ok: reasons.length === 0, reasons };
}
